import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  duration,
  easing,
  gradient,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  status,
  transition,
  typography,
} from '../../style-utils/theme';
import { shimmer, skeletonBackground } from '../../style-utils/motion';
import { down, shortLandscape, touch } from '../../style-utils/media-queries';
import { InlineMessage, m } from '../../motion';

/* Shared surfaces + primitives for the signup / onboarding / verify flows.
 * Everything here resolves to design tokens — no raw hex or magic px. */

/* Prop-reader helpers. Kept as block-body functions (not inline arrow-ternaries
 * in the template) so each dynamic value stays a bare `${fn}` interpolation —
 * short, and formatted identically by every prettier version. */
type ErrProps = { hasError?: boolean };
const inputBorder = (p: ErrProps) => {
  return p.hasError ? palette.alert : palette.line;
};
const inputShadow = (p: ErrProps) => {
  return p.hasError ? shadow.focusError : shadow.xs;
};
const inputHoverBorder = (p: ErrProps) => {
  return p.hasError ? palette.alert : palette.lineStrong;
};
const inputFocusBorder = (p: ErrProps) => {
  return p.hasError ? palette.alert : palette.iris;
};
const inputFocusShadow = (p: ErrProps) => {
  return p.hasError ? shadow.focusError : shadow.focus;
};
type CardProps = { wide?: boolean };
const cardMaxWidth = (p: CardProps) => {
  return p.wide ? '640px' : '480px';
};

type SelProps = { selected?: boolean };
const choiceBg = (p: SelProps) => {
  return p.selected ? palette.irisTint : palette.surface;
};
const choiceBorder = (p: SelProps) => {
  return p.selected ? palette.iris : palette.line;
};
const choiceShadow = (p: SelProps) => {
  return p.selected ? shadow.sm : shadow.xs;
};

// Amber for a draft that only exists on this device, green once the server has
// it — the dot is the whole difference between the two states.
type DraftToneProps = { tone?: 'saved' | 'local' };
const draftDot = (p: DraftToneProps) => {
  return p.tone === 'local' ? palette.warning : palette.success;
};

type ToneProps = { tone?: 'brand' | 'success' | 'warning' };
const bannerBg = (p: ToneProps) => {
  if (p.tone === 'success') return palette.successSoft;
  if (p.tone === 'warning') return palette.warningSoft;

  return palette.irisTintSoft;
};
const bannerBorder = (p: ToneProps) => {
  if (p.tone === 'success') return palette.success;
  if (p.tone === 'warning') return palette.warning;

  return palette.irisTint;
};

/* THE PUBLIC AND ACCOUNT SCREENS HAD NO <main> AND NO SKIP TARGET.
   Measured on /, /signup, /forgot-password and /account/profile: zero <main>
   elements, and on the two inside the authenticated shell a globally-mounted
   skip link pointing at an id nothing carried.

   The nine screens built on this Shell are all page-level, so the landmark
   belongs on the Shell itself rather than at nine call sites — the same
   argument layout-container.styled.tsx records for ContentWrapper, and for the
   same reason: the tenth call site would not remember. */
const ShellBase = styled.main`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  padding: ${spacing.xxxl} ${spacing.xl};
  background: ${palette.porcelain};
  overflow-x: hidden;
  isolation: isolate;

  /* Soft ambient iris wash — the same depth cue as the login screen. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(
        58% 55% at 16% 10%,
        ${palette.tealWash} 0%,
        ${palette.tealTransparent} 60%
      ),
      radial-gradient(
        52% 50% at 90% 94%,
        ${palette.tealWashSoft} 0%,
        ${palette.tealTransparent} 62%
      );
  }

  ${down('sm')} {
    padding: ${spacing.sm};
    align-items: stretch;
  }

  /* A phone held sideways is 430px tall at most. 64px of top and bottom padding
     is a third of that spent before the wizard's first field, and the shell is
     top-anchored so the cost is not recovered by centring. Horizontal padding is
     untouched: sideways is the axis with room to spare. */
  ${shortLandscape} {
    padding: ${spacing.sm} ${spacing.xl};
  }
`;

/* The id and tabIndex are supplied HERE, not at the call sites. tabIndex -1 is
   what makes the region able to RECEIVE the skip link's jump: without it the
   browser moves the URL fragment and leaves focus where it was. */
export const Shell: React.FC<React.ComponentProps<typeof ShellBase>> = ({
  id = 'main-content',
  tabIndex = -1,
  ...rest
}) => <ShellBase id={id} tabIndex={tabIndex} {...rest} />;

export const Card = styled.div<{ wide?: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${cardMaxWidth};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.lg};
  padding: ${spacing.xl};

  ${down('sm')} {
    padding: ${spacing.lg} ${spacing.md};
    border-radius: ${radius.md};
  }

  ${shortLandscape} {
    padding: ${spacing.md} ${spacing.lg};
  }
`;

/* The per-step entrance used to live here as a fadeInUp keyframe on a div
   remounted per step. It is now the shared motion StepTransition
   (src/motion/step-transition.tsx): directional, with an exit, inside an
   AutoHeight card. STEP_LAYER_STYLE is what the wizards pass as its style. */
export const STEP_LAYER_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

export const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.lg};
`;

export const Wordmark = styled.span`
  font-family: ${typography.display};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.heavy};
  letter-spacing: ${typography.tracking.tight};
  color: ${palette.ink};
`;

export const Heading = styled.h1`
  margin: 0;
  font-family: ${typography.display};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.heavy};
  line-height: ${typography.leading.snug};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

export const Subheading = styled.p`
  margin: ${spacing.xs} 0 ${spacing.lg};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.regular};
  line-height: ${typography.leading.normal};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.slate};
`;

/* ── form primitives (match the login field spec) ── */

export const Field = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: ${spacing.sm};
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.sm};

  ${down('sm')} {
    grid-template-columns: 1fr;
  }
`;

export const Label = styled.label`
  margin-bottom: ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.inkSoft};
`;

const fieldBase = css`
  width: 100%;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  letter-spacing: ${typography.tracking.snug};
  transition: ${transition.colors};
`;

/* The refusal shake is no longer a CSS keyframe on the input: it replayed
   only when hasError flipped false to true, so a second refusal of the same
   field was silent. Sites wrap the input in the motion Shake (attempt-keyed)
   instead. */
export const Input = styled.input<ErrProps>`
  ${fieldBase};
  border: 1px solid ${inputBorder};
  box-shadow: ${inputShadow};

  &::placeholder {
    color: ${palette.slateLight};
  }
  &:hover {
    border-color: ${inputHoverBorder};
  }
  &:focus {
    outline: none;
    border-color: ${inputFocusBorder};
    box-shadow: ${inputFocusShadow};
  }
`;

/* ── City autocomplete ──
   Wraps the City <input> so the suggestion list can anchor to it. The field
   itself is unchanged — the signup modal is held to exact mockup parity, so
   this adds a dropdown and nothing else. */
export const CityField = styled.div`
  position: relative;
  width: 100%;
`;

export const CitySuggestionList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 220px;
  overflow-y: auto;
  padding: 4px;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  box-shadow: ${shadow.md};
`;

export const CitySuggestionItem = styled.button`
  display: block;
  width: 100%;
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs} ${spacing.sm};
  border: 0;
  border-radius: ${radius.xs};
  background: none;
  text-align: left;
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
  }

  /* Same correction as the building address list: hover and keyboard focus were
     one rule that also removed the outline, so the focused city suggestion was
     a 1.19:1 tint and looked identical to whatever the mouse was over. */
  &:focus-visible {
    background: ${palette.irisTintSoft};
    outline: none;
    box-shadow: ${shadow.focus};
  }

  /* The option the ARROW KEYS are on. Focus stays in the text box (the list is
     driven through aria-activedescendant), so this is the only thing that
     shows a keyboard user where they are: the same ring a focused option gets. */
  &[data-active] {
    background: ${palette.irisTintSoft};
    box-shadow: ${shadow.focus};
  }
`;

// The address provider's credit, at the foot of the city suggestions. Its free
// plan requires a link shown where its results are.
export const CitySuggestionCredit = styled.a`
  display: flex;
  align-items: center;
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs} ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
  text-decoration: underline;
  border-radius: ${radius.xs};

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const Select = styled.select`
  ${fieldBase};
  border: 1px solid ${palette.line};
  box-shadow: ${shadow.xs};
  cursor: pointer;
  &:hover {
    border-color: ${palette.lineStrong};
  }
  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

/* Wrapper that gives a Stripe split Element (CardNumber/Expiry/Cvc) the exact
   look of our own inputs — the iframe renders inside, we own the border + focus
   ring. Reuses the same error/hover/focus prop-readers as <Input>. */
export const StripeField = styled.div<ErrProps>`
  display: flex;
  align-items: center;
  width: 100%;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  border: 1px solid ${inputBorder};
  box-shadow: ${inputShadow};
  transition: ${transition.colors};

  & > div {
    width: 100%;
  }
  &:hover {
    border-color: ${inputHoverBorder};
  }
  &:focus-within {
    border-color: ${inputFocusBorder};
    box-shadow: ${inputFocusShadow};
  }
`;

/* Password field with an inline show/hide text toggle. */
export const PasswordWrap = styled.div`
  position: relative;
  width: 100%;
`;

export const PasswordInput = styled(Input)`
  padding-right: 68px;
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: ${spacing.xs};
  top: 0;
  min-width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  padding: 0 ${spacing.xs};
  border: none;
  background: transparent;
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  border-radius: ${radius.xs};
  transition: ${transition.colors};
  &:hover {
    color: ${palette.lagoon};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/* ── buttons ── */

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  height: ${layout.buttonHeight};
  padding: 0 ${spacing.lg};
  border-radius: ${radius.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  cursor: pointer;
  transition:
    ${transition.colors},
    transform ${duration.fast} ${easing.spring};
  & svg {
    transition: transform ${duration.fast} ${easing.standard};
  }
  &:active {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
  &:disabled {
    cursor: default;
    opacity: 0.6;
    transform: none;
    box-shadow: none;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase};
  border: none;
  background: ${gradient.primary};
  box-shadow: ${shadow.brand};
  color: ${palette.white};
  &:hover:not(:disabled) {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }
  &:hover:not(:disabled) svg {
    transform: translateX(3px);
  }
  &:disabled {
    background: ${palette.lineStrong};
    color: ${palette.slateLight};
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const SecondaryButton = styled.button`
  ${buttonBase};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  color: ${palette.ink};
  &:hover:not(:disabled) {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lagoon};
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const GhostButton = styled.button`
  ${buttonBase};
  background: transparent;
  border: none;
  color: ${palette.lagoon};
  padding: 0 ${spacing.sm};
  &:hover:not(:disabled) {
    background: ${palette.irisTintSoft};
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/**
 * ONE ACTION, ON ITS OWN LINE, CENTRED.
 *
 * For the step-level escape hatch that is not part of the row above it: on the
 * invite steps the row is "Send invite | Continue" — both about THIS step — and
 * "Skip invitations" leaves the sequence entirely. Putting it in the same row
 * made it read as a third peer of the other two.
 */
export const CenteredAction = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${spacing.sm};

  ${down('sm')} {
    & > * {
      width: 100%;
    }
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  margin-top: ${spacing.lg};

  ${down('sm')} {
    flex-direction: column-reverse;
    align-items: stretch;
    & > * {
      width: 100%;
    }
  }
`;

export const FullWidthButton = styled(PrimaryButton)`
  width: 100%;
  margin-top: ${spacing.sm};
`;

/* ── repeatable rows (the portfolios step) ──
 *
 * A list of name inputs, each with a remove control beside it. The remove
 * button is a full ${layout.fieldHeight} square rather than a bare glyph so it
 * clears the 44px touch target the design system requires. */

export const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin-bottom: ${spacing.sm};
`;

export const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.xs};
`;

export const RowMain = styled.div`
  flex: 1;
  min-width: 0;
`;

export const RowBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  margin-left: ${spacing.xs};
  padding: 0 ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${palette.irisTint};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.irisDeep};
`;

export const RowRemoveButton = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${layout.fieldHeight};
  height: ${layout.fieldHeight};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.slate};
  cursor: pointer;
  transition: ${transition.colors};
  &:hover:not(:disabled) {
    border-color: ${palette.alert};
    background: ${palette.alertSoft};
    color: ${status.alert.fg};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

export const AddRowButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  width: 100%;
  height: ${layout.fieldHeight};
  border: 1px dashed ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: transparent;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.lagoon};
  cursor: pointer;
  transition: ${transition.colors};
  &:hover:not(:disabled) {
    border-color: ${palette.lagoon};
    background: ${palette.irisTintSoft};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

/* ── draft state ──
 *
 * The quiet "we kept what you typed" line under a step. Deliberately understated
 * — it is reassurance, not an action. */

export const SavedNote = styled.p<DraftToneProps>`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  /* The note is ALWAYS in the tree now — it used to enter and leave as the
     draft cycled idle → local → syncing → synced, and everything under it
     moved each time. The reserved line box (one grid step clear of the
     13px/1.5 text) is what keeps the step's height constant when the text is
     empty. Only ever consumed by the portfolios step. */
  min-height: ${spacing.md};
  margin: ${spacing.xs} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.slate};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: ${radius.circle};
    background: ${draftDot};
  }

  /* No text means nothing to report, so the dot goes too — a lone coloured
     pip with no wording reads as a fault indicator. */
  &:empty::before {
    display: none;
  }
`;

/* ── progress ── */

export const ProgressWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  width: 100%;
  margin-bottom: ${spacing.lg};
`;

export const ProgressLabel = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slate};
`;

export const ProgressTrack = styled.div`
  position: relative;
  height: 8px;
  width: 100%;
  background: ${palette.line};
  border-radius: ${radius.pill};
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ pct: number }>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: ${({ pct }) => Math.max(0, Math.min(100, pct))}%;
  background: ${gradient.primary};
  border-radius: ${radius.pill};
  box-shadow: ${shadow.sm};
  transition: width ${duration.slow} ${easing.entrance};
`;

/* ── segmented progress (mockup: N discrete bars that light up per step) ── */

export const ProgressSegments = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  margin-bottom: ${spacing.lg};
`;

/* Each segment is a 4px track holding a fill that SCALES in from the left
   (motion, T.base) rather than a background colour that tints. The current
   segment is drawn a shade deeper than the completed ones: emphasis without a
   pulse. */
export const ProgressSegment = styled.div`
  position: relative;
  flex: 1;
  height: 4px;
  border-radius: ${radius.pill};
  background: ${palette.line};
  overflow: hidden;
`;

const segmentFill = (p: { $current?: boolean }) => {
  return p.$current ? palette.lagoon : palette.iris;
};

export const ProgressSegmentFill = m.div<{ $current?: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: ${radius.pill};
  background: ${segmentFill};
  transform-origin: left center;
  transition: background-color ${duration.base} ${easing.standard};
`;

/* ── plan option rows (mockup .plan-pick .opt: compact single-line choice) ── */

export const PlanOptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: ${spacing.sm};
`;

export const PlanOption = styled.button<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  width: 100%;
  text-align: left;
  padding: 16px 16px;
  border-radius: ${radius.sm};
  border: 1.5px solid ${choiceBorder};
  background: ${({ selected }) => (selected ? palette.irisTintSoft : palette.surface)};
  cursor: pointer;
  transition: ${transition.colors};
  &:hover {
    border-color: ${palette.lagoon};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const PlanOptionName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.display};
  font-weight: ${typography.weight.bold};
  color: ${palette.ink};
`;

export const PlanOptionPrice = styled.span`
  margin-left: auto;
  font-family: ${typography.ui};
  font-weight: ${typography.weight.semibold};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

/* ── choice / plan cards ── */

export const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.sm};
`;

export const ChoiceCard = styled.button<{ selected?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  text-align: left;
  width: 100%;
  padding: ${spacing.md};
  border-radius: ${radius.md};
  background: ${choiceBg};
  border: 1.5px solid ${choiceBorder};
  box-shadow: ${choiceShadow};
  cursor: pointer;
  transition:
    ${transition.colors},
    transform ${duration.fast} ${easing.spring};
  &:hover {
    border-color: ${palette.lagoon};
    transform: translateY(-2px);
    box-shadow: ${shadow.md};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const ChoiceTitle = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const ChoiceMeta = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  line-height: ${typography.leading.normal};
`;

export const PlanPrice = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  color: ${palette.lagoon};
`;

/* ── misc text ── */

/* status.alert.fg (#B23127, 5.34:1) rather than palette.alert (#F0564A,
   3.42:1). This is small text — 12px medium — so it is measured at 4.5:1, and
   the one line on the page that tells somebody why they cannot continue is a
   poor place to be under the floor. */
const errorTextCss = css`
  margin: ${spacing.xs} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  color: ${status.alert.fg};
`;

const ErrorTextBase = styled.p`
  ${errorTextCss};
`;

/**
 * THE ERROR LINE THAT UNFOLDS. Same typography and the same role="alert" as
 * ErrorText, but mounted through the motion InlineMessage so the space under
 * the field opens and closes instead of the button below jumping. Pass the
 * message (or nothing): an empty message closes it. The role sits on the inner
 * element ONCE; nothing here nests a second alert.
 */
export const FieldError = styled(InlineMessage)`
  ${errorTextCss};
`;

/**
 * THE QUIET LINE UNDER A FIELD. Same position and rhythm as FieldError, in the
 * ordinary text colour: this is information about what was typed, not a
 * problem with it. Used for the signup unit count's running total, where the
 * number the visitor enters sets the bill and the price was previously three
 * steps away.
 *
 * `aria-live="polite"` and not role="alert": it updates on every keystroke,
 * and an assertive announcement per character is unusable.
 */
export const FieldHint = styled.p`
  margin: ${spacing.half} 0 0;
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;
FieldHint.defaultProps = { 'aria-live': 'polite' } as Record<string, unknown>;

/**
 * EVERY ERROR ON THE PUBLIC SURFACE IS ANNOUNCED.
 *
 * Across signup, forgot-password, reset-password, invite-accept and the Stripe
 * panel there were TWENTY error renders and exactly one of them — login's —
 * carried role="alert". Everything else was a plain <p>: drawn, never spoken.
 * A screen-reader user pressed Continue, nothing appeared to happen, and the
 * reason was sitting silently on screen.
 *
 * Defaulted here rather than added at twenty call sites, because the twenty-first
 * is the one that would have been forgotten. `role` stays overridable for the
 * rare case that wants role="status" — an error that is expected and not urgent.
 *
 * Safe to announce on mount: every one of these renders conditionally, so the
 * element appearing IS the event worth announcing.
 */
export const ErrorText: React.FC<
  React.ComponentProps<typeof ErrorTextBase>
> = ({ role = 'alert', ...rest }) => <ErrorTextBase role={role} {...rest} />;

export const HelperText = styled.p`
  margin: ${spacing.sm} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export const TopLink = styled.p`
  margin: ${spacing.md} 0 0;
  text-align: center;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};

  & a,
  & button {
    color: ${palette.lagoon};
    font-weight: ${typography.weight.semibold};
    background: none;
    border: none;
    cursor: pointer;
    font-size: inherit;
    font-family: inherit;
    padding: 0;

    /* 17px OF HIT AREA ON THE PRODUCT'S MOST-USED SCREEN. Measured at 320px on
       sign-in and sign-up by scripts/wp/verify-untested-surfaces-live.js:
       "Forgot your password?", "Create an account" and "Sign in" were 17px tall
       — the line box and nothing else, because of the padding: 0 immediately
       above. That is half the un-tappable floor, let alone the 44px
       touchTarget, on the two screens every customer has to get through and the
       one a locked-out person needs most.

       AFTER that padding, not before it: the first attempt put this higher in
       the rule and padding: 0 simply won on source order, taking the links from
       17px to 21px instead of to 44. Same specificity means later wins.
       Only where there is a finger; on a desk these are unchanged. */
    ${touch} {
      display: inline-block;
      padding: ${spacing.sm} ${spacing.xs};
      min-height: ${layout.touchTarget};
    }
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const InfoBanner = styled.div<{
  tone?: 'brand' | 'success' | 'warning';
}>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${spacing.md};
  border-radius: ${radius.md};
  margin-bottom: ${spacing.md};
  background: ${bannerBg};
  border: 1px solid ${bannerBorder};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.inkSoft};
  line-height: ${typography.leading.normal};
`;

/* ── success screen (mockup step 5) ── */

export const SuccessBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${spacing.sm} 0 ${spacing.xs};
`;

/* One line of the success screen, carrying its arrival variants. Full width
   so the button inside it keeps its width under the centred flex column. */
export const SuccessRow = m.div`
  width: 100%;
`;

export const SuccessIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: ${spacing.md};
  border-radius: ${radius.circle};
  background: ${palette.successSoft};
  color: ${status.success.fg};
  font-size: ${typography.size.xxl}px;
  font-weight: ${typography.weight.bold};
`;

/* ── order summary (payment step) ── */

export const SummaryBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${spacing.md};
  border-radius: ${radius.md};
  background: ${palette.porcelain};
  border: 1px solid ${palette.line};
  margin-bottom: ${spacing.md};
`;

export const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

export const SummaryValue = styled.span`
  color: ${palette.ink};
  font-weight: ${typography.weight.medium};
  text-align: right;
`;

export const SummaryTotal = styled(SummaryRow)`
  margin-top: ${spacing.half};
  padding-top: ${spacing.sm};
  border-top: 1px solid ${palette.line};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.bold};
  color: ${palette.ink};

  & > span {
    color: ${palette.ink};
  }
`;

/* ── password strength meter ── */

type StrengthProps = { score: number };
const strengthPct = (p: StrengthProps) => {
  const clamped = Math.max(0, Math.min(4, p.score));

  return `${(clamped / 4) * 100}%`;
};
const strengthColor = (p: StrengthProps) => {
  if (p.score >= 4) return palette.success;
  if (p.score >= 3) return palette.iris;
  if (p.score >= 2) return palette.warning;

  return palette.alert;
};

export const StrengthTrack = styled.div`
  position: relative;
  height: 6px;
  width: 100%;
  margin-top: ${spacing.xs};
  background: ${palette.line};
  border-radius: ${radius.pill};
  overflow: hidden;
`;

export const StrengthFill = styled.div<StrengthProps>`
  height: 100%;
  width: ${strengthPct};
  background: ${strengthColor};
  border-radius: ${radius.pill};
  transition:
    width ${duration.base} ${easing.standard},
    background ${duration.base} ${easing.standard};
`;

export const StrengthText = styled.span<StrengthProps>`
  display: block;
  margin-top: ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  color: ${strengthColor};
`;

/* ── skeletons ──
 *
 * Placeholders shaped like the content they stand in for, so a card that is
 * loading is already the height it will be. The shimmer is the same one the
 * dashboard skeletons use; the global reduced-motion rule in index.css stops
 * it for readers who asked for less. */

const shimmerCss = css`
  ${skeletonBackground};
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const lineWidth = (p: { w?: string }) => {
  return p.w || '100%';
};

export const SkeletonLine = styled.span<{ w?: string }>`
  display: block;
  width: ${lineWidth};
  max-width: 100%;
  height: ${spacing.sm};
  border-radius: ${radius.pill};
  ${shimmerCss};
`;

export const SkeletonField = styled.span`
  display: block;
  width: 100%;
  height: ${layout.fieldHeight};
  border-radius: ${radius.xs};
  ${shimmerCss};
`;

export const SkeletonButton = styled.span`
  display: block;
  width: 100%;
  height: ${layout.buttonHeight};
  margin-top: ${spacing.sm};
  border-radius: ${radius.sm};
  ${shimmerCss};
`;

/* The height of one PlanOption: 16px padding above and below a ~24px line,
   inside a 1.5px border. */
export const SkeletonOption = styled.span`
  display: block;
  width: 100%;
  height: calc(${spacing.sm} * 2 + ${spacing.md});
  border-radius: ${radius.sm};
  ${shimmerCss};
`;

const SkeletonFieldWrap = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  width: 100%;
  margin-bottom: ${spacing.sm};
`;

/* One label line over one field box: the footprint of a <Field>. */
export const FieldSkeleton: React.FC = () => (
  <SkeletonFieldWrap aria-hidden="true">
    <SkeletonLine w="32%" />
    <SkeletonField />
  </SkeletonFieldWrap>
);

/* Wraps a set of skeleton pieces and says what is loading to assistive tech. */
export const SkeletonGroup: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div role="status" aria-busy="true" aria-label={label}>
    {children}
  </div>
);

/* Heading + subheading footprint, for a card whose title is not yet known. */
export const HeadingSkeleton: React.FC = () => (
  <SkeletonFieldWrap aria-hidden="true" style={{ marginBottom: spacing.lg }}>
    <SkeletonLine w="58%" style={{ height: spacing.md }} />
    <SkeletonLine w="88%" />
  </SkeletonFieldWrap>
);

/* Pill badge used to mark the AI (IQ) plan in the picker. */
export const IqBadge = styled.span`
  display: inline-block;
  margin-left: ${spacing.xs};
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${gradient.primary};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.caps};
`;
